import {SxProps, TextField, TextFieldProps, Tooltip} from "@mui/material";
import React, {useEffect, useState} from "react";
import {clamp, ToInt, ToMoney} from "@shared/MathHelp.ts";

export type InputEntryTypes = 'String' |
    'Float' |
    'Bool' |
    'Date' |
    'Int' |
    'Percent' |
    'Money';

export interface InputEntryType {
}


export interface InputEntryProps<T = InputEntryType> {
    sx?: SxProps;
    textFieldProps?: TextFieldProps;
    onChange?: (value: T) => void;
    value: T;
    label?: string;
    clampValues?: { min?: number, max?: number };
    disabled?: boolean;
    onEnter?: (value: T) => void;
    tooltip?: string | React.ReactNode;
}


export function MoneyInputEntry(props: InputEntryProps<number | undefined>) {
    const [numericValue, setNumericValue] = useState<number | undefined>(props.value || undefined);
    const [rawInputValue, setRawInputValue] = useState<string>('');
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Format the value for display based on focus state
    const getDisplayValue = () => {
        if (isFocused) {
            // When focused, show the raw input value or the numeric value as a plain number
            return rawInputValue || (numericValue ? numericValue.toString() : '');
        } else {
            // When not focused, show the formatted money value
            return ToMoney(numericValue) || '';
        }
    };

    const handleChange = (c: React.ChangeEvent<HTMLInputElement>) => {
        // Store the raw input value
        const inputValue = c.target.value.replace(/[^\d,.]/g, '');
        setRawInputValue(inputValue);

        // Only allow numeric input, commas, and periods for parsing
        const cleanInput = inputValue.replace(/[^\d,.]/g, '');

        // Check if the input contains a decimal separator
        const hasDecimalSeparator = cleanInput.includes(',') || cleanInput.includes('.');

        // Parse the numeric value from the input
        let newNumericValue: number | undefined;

        if (hasDecimalSeparator) {
            // If it has a decimal separator, parse it directly
            // Replace comma with period for proper parsing
            const normalizedInput = cleanInput.replace(/,/g, '.');
            newNumericValue = normalizedInput ? parseFloat(normalizedInput) : undefined;
        } else {
            // If no decimal separator, treat as whole number
            const cleanValue = cleanInput.replace(/[^\d]/g, '');
            newNumericValue = cleanValue ? parseInt(cleanValue, 10) : undefined;
        }
        if (newNumericValue !== undefined && props.clampValues) {
            newNumericValue = Math.max(props.clampValues.min || 0, Math.min(props.clampValues.max || Infinity, newNumericValue));
        }

        // Update the numeric value and call the onChange callback
        setNumericValue(newNumericValue);
        if (props.onChange) props.onChange(newNumericValue);
    };

    // Handle focus state
    const handleFocus = () => {
        setIsFocused(true);
        // When focusing, set the raw input to the numeric value for easier editing
        setRawInputValue(numericValue ? numericValue.toString() : '');
    };

    const handleBlur = () => {
        setIsFocused(false);
        // When blurring, clear the raw input value
        setRawInputValue('');
    };

    // Update the values when props.value changes externally
    useEffect(() => {
        if (numericValue != props.value) {
            setNumericValue(props.value || undefined);
            if (isFocused) {
                setRawInputValue(props.value ? props.value.toString() : '');
            }
        }
    }, [props.value]);

    return (
        <Tooltip title={props.tooltip}>
            <TextField fullWidth
                       {...props.textFieldProps}
                       label={props.label ?? ''}
                       required={false}
                       onChange={handleChange}
                       onFocus={handleFocus}
                       onBlur={handleBlur}
                       onKeyDown={(e) => {
                           if (e.key === 'Enter' && props.onEnter) {
                               props.onEnter(numericValue);
                           }
                       }}
                       value={getDisplayValue()}
                       disabled={(props.disabled || !props.onChange) ?? false}
                       sx={{...props.sx}}
                       slotProps={{
                           htmlInput: {
                               inputMode: 'numeric'
                           },
                           ...props.textFieldProps?.slotProps
                       }}
                       inputRef={inputRef}
            /></Tooltip>
    );
}

export function IntInputEntry(props: InputEntryProps<number>) {
    const [numericValue, setNumericValue] = useState<number>(props.value || 0);
    const [rawInputValue, setRawInputValue] = useState<string>('');
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Format the value for display based on focus state
    const getDisplayValue = () => {
        if (isFocused) {
            // When focused, show the raw input value or the numeric value as a plain number
            return rawInputValue || (numericValue ? numericValue.toString() : '');
        } else {
            // When not focused, show the formatted money value
            return numericValue || '';
        }
    };

    const handleChange = (c: React.ChangeEvent<HTMLInputElement>) => {
        // Store the raw input value
        const inputValue = c.target.value.replace(/[^\d]/g, '');
        setRawInputValue(props.clampValues != null ? clamp(ToInt(inputValue), props.clampValues.min ?? ToInt(inputValue), props.clampValues.max ?? ToInt(inputValue)).toString() : inputValue);

        // Only allow numeric input
        const cleanInput = inputValue.replace(/[^\d]/g, '');

        // Parse the numeric value from the input
        let newNumericValue: number;

        newNumericValue = cleanInput ? parseInt(cleanInput, 10) : 0;
        newNumericValue = Math.min(props.clampValues?.max ?? newNumericValue, newNumericValue);
        newNumericValue = Math.max(props.clampValues?.min ?? newNumericValue, newNumericValue);

        // Update the numeric value and call the onChange callback
        setNumericValue(newNumericValue);
        if (props.onChange)
            props.onChange(newNumericValue);
    };

    // Handle focus state
    const handleFocus = () => {
        setIsFocused(true);
        // When focusing, set the raw input to the numeric value for easier editing
        setRawInputValue(numericValue ? numericValue.toString() : '');
    };

    const handleBlur = () => {
        setIsFocused(false);
        // When blurring, clear the raw input value
        setRawInputValue('');
    };

    // Update the values when props.value changes externally
    useEffect(() => {
        if (numericValue != props.value) {
            setNumericValue(props.value || 0);
            if (isFocused) {
                setRawInputValue(props.value ? props.value.toString() : '');
            }
        }
    }, [props.value]);

    return (
        <TextField fullWidth
                   {...props.textFieldProps}
                   label={props.label ?? ''}
                   required={false}
                   onChange={handleChange}
                   onFocus={handleFocus}
                   onBlur={handleBlur}
                   onKeyDown={(e) => {
                       if (e.key === 'Enter' && props.onEnter) {
                           props.onEnter(numericValue);
                       }
                   }}
                   value={getDisplayValue()}
                   sx={{...props.sx}}
                   slotProps={{
                       ...props.textFieldProps?.slotProps,
                       htmlInput: {
                           inputMode: 'numeric',
                       }
                   }}
                   inputRef={inputRef}
        />
    );

}